import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  LayersControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useState } from "react";
import Legend from "./Surveyor/Legend";
import ChoroplethLegend from "./Surveyor/ChoropletLegend";

const getStatusClass = (status) => {
  if (!status) return "status-default";

  const lowerStatus = status.toLowerCase().trim();

  if (lowerStatus === "closed") return "status-closed";
  if (lowerStatus === "on progress") return "status-on-progress";
  if (lowerStatus === "not started") return "status-not-started";
  if (lowerStatus === "valid") return "status-valid";

  return "status-default";
};

const CustomPopupStyles = () => {
  return (
    <style>{`
      .styled-popup .leaflet-popup-content-wrapper {
        background: #ffffff;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

        border: 2px solid #6c757d;
      }

      .styled-popup .leaflet-popup-content {
        color: #343a40;
        margin: 16px;
        width: auto !important;
        min-width: 240px;
      }
      
      .custom-popup-content {
        font-family: Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
      }
      .custom-popup-content div { margin-bottom: 4px; }
      .custom-popup-content div:last-child { margin-bottom: 0; }
      .custom-popup-content strong { font-weight: 600; }

      /* Closed (Green) */
      .styled-popup.status-closed .leaflet-popup-content-wrapper {
        border-color: #63FF78;
      }

      /* On Progress (Yellow) */
      .styled-popup.status-on-progress .leaflet-popup-content-wrapper {
        border-color: #FAED6F;
      }

      /* Not Started (Red) */
      .styled-popup.status-not-started .leaflet-popup-content-wrapper {
        border-color: #e8293cff;
      }

      /* Valid (Blue) */
      .styled-popup.status-valid .leaflet-popup-content-wrapper {
        border-color: #60E8D3;
      }

      /* Default (Grey) - for fallback */
      .styled-popup.status-default .leaflet-popup-content-wrapper {
        border-color: #6c757d;
      }
    `}</style>
  );
};

function ChangeView({ center, zoom }) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}

export default function MapLayout({
  position,
  zoom,
  scrollWheelZoom,
  geoJSONData,
  onEachFeature,
  styleFeature,
  styleContainer,
  markersData,
  renderDataDetail,
  pathOptions,
  needLegend,
  needChoropleth
}) {
  const [activeBaseLayer, setActiveBaseLayer] = useState("Map");

  const mapStyle = {
    ...(styleContainer || {}),
    backgroundColor: activeBaseLayer === "Map" ? "white" : "transparent",
  };

  if (!position || position.length !== 2) {
    return <div>Loading map...</div>;
  }

  return (
    <>
      <CustomPopupStyles />
      {needLegend && <Legend />}
      <MapContainer
        center={position}
        zoom={zoom}
        scrollWheelZoom={scrollWheelZoom}
        style={mapStyle}
      >
        <ChangeView center={position} zoom={zoom} />

        <LayersControl position="topright">
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap.HOT">
            <TileLayer
              attribution="&copy; OpenStreetMap contributors, Tiles style by Humanitarian OpenStreetMap Team hosted by OpenStreetMap France"
              url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenTopoMap">
            <TileLayer
              attribution="Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)"
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked name="Map">
            <TileLayer url="" />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay checked name="GeoJSON Layer">
            {geoJSONData && (
              <GeoJSON
                data={geoJSONData}
                onEachFeature={onEachFeature}
                style={styleFeature}
                pathOptions={pathOptions}
              />
            )}
          </LayersControl.Overlay>

          {markersData?.data?.map((data) => {
            const isValidLocation =
              data.latitude !== null &&
              data.longitude !== null &&
              !isNaN(data.latitude) &&
              !isNaN(data.longitude);
            if (!isValidLocation) return null;

            const statusClass = getStatusClass(data.store_status);

            return (
              <Marker
                position={[data.latitude, data.longitude]}
                key={data.id}
                icon={data.icon}
              >
                <Popup className={`styled-popup ${statusClass}`}>
                  {renderDataDetail(data)}
                </Popup>
              </Marker>
            );
          })}
        </LayersControl>
        {needChoropleth && <ChoroplethLegend />}
      </MapContainer>
    </>
  );
}
